/**
 * Lock Screen
 * Fullscreen overlay for screen lock functionality
 */

import { ToasterService } from '@/services/ToasterService';
import { loadOEMTemplate } from '@/services/OEMUtils';
import { ZIndex } from '@/constants/ZIndex';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('LockScreen');

const LOCK_SCREEN_OVERLAY_ID = 'lock-screen-overlay';

/**
 * Show the lock screen overlay
 */
export async function showLockScreen(): Promise<void> {
  try {
    const html = await loadOEMTemplate('/forms/auth/lock-screen.html');

    // Create fullscreen overlay using ZIndex constant
    const overlay = document.createElement('div');
    overlay.id = LOCK_SCREEN_OVERLAY_ID;
    overlay.className = `fixed inset-0 bg-base-100`;
    overlay.style.zIndex = String(ZIndex.OVERLAY);
    overlay.innerHTML = html;
    document.body.appendChild(overlay);

    setupLockScreenForm();
    logger.debug('Lock screen shown');
  } catch (error) {
    logger.error('Error loading lock screen:', error);
    ToasterService.error('Failed to load lock screen');
  }
}

/**
 * Hide the lock screen overlay
 */
export function hideLockScreen(): void {
  const overlay = document.getElementById(LOCK_SCREEN_OVERLAY_ID);
  if (overlay) {
    overlay.remove();
    logger.debug('Lock screen hidden');
  }
}

/**
 * Setup the lock screen form submission handler
 */
function setupLockScreenForm(): void {
  const form = document.getElementById('lock-screen-form') as HTMLFormElement;
  const btnCancel = document.getElementById('lock-screen-cancel');

  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const password = formData.get('password') as string;

      if (!password) {
        ToasterService.warning('Please enter your password');
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      const originalText = submitBtn.innerHTML;
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Unlocking...';

      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        hideLockScreen();
        ToasterService.success('Screen unlocked successfully');
      } catch (error) {
        logger.error('Failed to unlock:', error);
        ToasterService.error('Failed to unlock. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
      }
    });
  }

  if (btnCancel) {
    btnCancel.addEventListener('click', () => {
      hideLockScreen();
    });
  }
}
